import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import TenantPickerLayout from '../../Layout/TenantPickerLayout';
import Logo from '../../Logo/Logo';
import useStyles from './EarlyAccessCreateUserPage.styles';
import RequiredFieldsNote from '../../RequiredFieldsNote/RequiredFieldsNote';
import NameField from './NameField';
import UsernameField from './UsernameField';
import SchoolAssociationField from './SchoolAssociationField';
import CreateUserButton from './CreateUserButton';
import nameFieldValidator from '../../../../util/nameFieldValidator';
import usernameValidator from '../../../../util/usernameValidator';
import { AUTO_COMPLETE } from '../../../../constants/constants';

const errorMessageIds = {
  usernameExists: 'earlyAccess.createUser.username.exists',
  failedToCreateUser: 'earlyAccess.createUser.message.error.failed',
  schoolAssociationRequired:
    'earlyAccess.createUser.schoolAssociation.required',
};

const EarlyAccessCreateUserPage = () => {
  const { classes } = useStyles();
  const { formatMessage } = useIntl();

  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    username: '',
    schoolAssociation: null,
  });

  const [touched, setTouched] = useState({});

  const firstNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.firstName',
  });
  const lastNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.lastName',
  });

  const getErrors = currentUserDetails => {
    const errors = {};

    const {
      isNameValid: isFirstNameValid,
      nameErrorMessageId: firstNameErrorMessageId,
    } = nameFieldValidator(currentUserDetails.firstName, 'firstName');

    const {
      isNameValid: isLastNameValid,
      nameErrorMessageId: lastNameErrorMessageId,
    } = nameFieldValidator(currentUserDetails.lastName, 'lastName');

    const { isUsernameValid, usernameErrorMessageId } = usernameValidator(
      currentUserDetails.username,
    );

    if (!isFirstNameValid) {
      errors.firstName = firstNameErrorMessageId;
    }

    if (!isLastNameValid) {
      errors.lastName = lastNameErrorMessageId;
    }

    if (!isUsernameValid) {
      errors.username = usernameErrorMessageId;
    }

    if (!currentUserDetails.schoolAssociation) {
      errors.schoolAssociation = errorMessageIds.schoolAssociationRequired;
    }

    return errors;
  };

  const handleChange = event => {
    event.persist();
    const keyOfValueToUpdate = event.target.id;
    const { value } = event.target;

    setUserDetails(currentUserDetails => {
      return {
        ...currentUserDetails,
        [keyOfValueToUpdate]: value,
      };
    });

    setTouched(currentTouched => {
      return {
        ...currentTouched,
        [keyOfValueToUpdate]: true,
      };
    });
  };

  const handleBlur = event => {
    event.persist();
    const keyOfValueToUpdate = event.target.id;

    setTouched(currentTouched => {
      return {
        ...currentTouched,
        [keyOfValueToUpdate]: true,
      };
    });
  };

  const handleSchoolChange = selectedSchool => {
    setUserDetails(currentUserDetails => {
      return {
        ...currentUserDetails,
        schoolAssociation: selectedSchool,
      };
    });

    setTouched(currentTouched => {
      return {
        ...currentTouched,
        schoolAssociation: true,
      };
    });
  };

  const handleSubmit = event => {
    event.preventDefault();
  };

  const errors = getErrors(userDetails);
  const isValid = Object.keys(errors).length === 0;

  return (
    <TenantPickerLayout>
      <header>
        <Logo />
        <div className={classes.title}>
          <Typography component="h1" variant="h3">
            <FormattedMessage id="earlyAccess.createUser.title" />
          </Typography>
          <RequiredFieldsNote />
        </div>
      </header>
      <main>
        <form className={classes.form} onSubmit={handleSubmit}>
          <NameField
            name={userDetails.firstName}
            nameLabel={firstNameLabel}
            errorMessageId={touched.firstName && errors.firstName}
            handleChange={handleChange}
            handleBlur={handleBlur}
            elementId="firstName"
          />
          <NameField
            name={userDetails.lastName}
            nameLabel={lastNameLabel}
            errorMessageId={touched.lastName && errors.lastName}
            handleChange={handleChange}
            handleBlur={handleBlur}
            elementId="lastName"
            autoComplete={AUTO_COMPLETE.FAMILY_NAME}
          />
          <UsernameField
            username={userDetails.username}
            errorMessageId={touched.username && errors.username}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <SchoolAssociationField
            selectedSchool={userDetails.schoolAssociation}
            errorMessageId={
              touched.schoolAssociation && errors.schoolAssociation
            }
            handleChange={handleSchoolChange}
          />
          <CreateUserButton isDisabled={!isValid} />
        </form>
      </main>
    </TenantPickerLayout>
  );
};

export default EarlyAccessCreateUserPage;
